/**
 * Request Details Page
 * Shows full details of a request with helper applications and integrated chat
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { requestsAPI, chatAPI, ratingsAPI, usersAPI, helpersAPI } from '../../services/api';
import * as socketService from '../../services/socket';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Ship,
  Users,
  Star,
  MessageCircle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  ChevronDown,
  ChevronUp,
  Award,
  Info,
  X,
  UserCheck,
  Edit2,
  FileText,
  Download,
} from 'lucide-react';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function RequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useNotifications();
  const { user } = useAuth();

  const [request, setRequest] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Group chat state (main chat with all helpers)
  const [groupChatData, setGroupChatData] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupChatLoading, setGroupChatLoading] = useState(false);
  const [groupNewMessage, setGroupNewMessage] = useState('');
  const [groupSendingMessage, setGroupSendingMessage] = useState(false);
  const groupMessagesEndRef = useRef(null);

  // Private chat state (modal for 1-on-1 with a helper)
  const [privateModalHelper, setPrivateModalHelper] = useState(null);
  const [privateChatData, setPrivateChatData] = useState(null);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [privateChatLoading, setPrivateChatLoading] = useState(false);
  const [privateNewMessage, setPrivateNewMessage] = useState('');
  const [privateSendingMessage, setPrivateSendingMessage] = useState(false);
  const privateMessagesEndRef = useRef(null);
  const [privateUnreadByHelper, setPrivateUnreadByHelper] = useState({}); // { odoo: true }

  // Rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingMode, setRatingMode] = useState('team'); // 'team' or 'individual'
  const [rating, setRating] = useState(0); // for team rating
  const [ratingComment, setRatingComment] = useState(''); // for team rating
  const [individualRatings, setIndividualRatings] = useState({}); // { odoo: { rating: 0, comment: '' } }
  const [currentIndividualIndex, setCurrentIndividualIndex] = useState(0); // which helper we're rating
  const [submittingRating, setSubmittingRating] = useState(false);

  // Documents state
  const [expandedDocs, setExpandedDocs] = useState(null);
  const [docsData, setDocsData] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(null);

  // More Info modal state
  const [showMoreInfo, setShowMoreInfo] = useState(null);
  const [moreInfoData, setMoreInfoData] = useState(null);
  const [loadingMoreInfo, setLoadingMoreInfo] = useState(false);

  useEffect(() => {
    loadRequestDetails();
  }, [id]);


  // Listen for real-time chat messages (group chat)
  useEffect(() => {
    if (!groupChatData?.id) return;

    const unsubscribe = socketService.on(socketService.EVENTS.NEW_MESSAGE, (message) => {
      if (message.conversation_id === groupChatData.id && message.sender_id !== user?.id) {
        setGroupMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        setTimeout(() => groupMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    });

    return () => unsubscribe();
  }, [groupChatData?.id, user?.id]);

  // Listen for real-time private chat messages
  useEffect(() => {
    if (!privateChatData?.id) return;

    const unsubscribe = socketService.on(socketService.EVENTS.NEW_MESSAGE, (message) => {
      if (message.conversation_id === privateChatData.id && message.sender_id !== user?.id) {
        setPrivateMessages(prev => {
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        setTimeout(() => privateMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    });

    return () => unsubscribe();
  }, [privateChatData?.id, user?.id]);

  // Listen for new private messages to show unread indicator
  useEffect(() => {
    const unsubscribe = socketService.on(socketService.EVENTS.NEW_MESSAGE, (message) => {
      // If message is not from us and not in the currently open modal
      if (message.sender_id !== user?.id && message.conversation_id !== groupChatData?.id) {
        // Check if modal is not open for this sender
        if (!privateModalHelper || privateModalHelper.user_id !== message.sender_id) {
          setPrivateUnreadByHelper(prev => ({ ...prev, [message.sender_id]: true }));
        }
      }
    });

    return () => unsubscribe();
  }, [user?.id, groupChatData?.id, privateModalHelper]);

  const loadRequestDetails = async () => {
    try {
      const [requestData, applicationsData] = await Promise.all([
        requestsAPI.getById(id),
        requestsAPI.getApplications(id),
      ]);
      const reqData = requestData.data || requestData.request;
      setRequest(reqData);
      const apps = applicationsData.data || applicationsData.applications || [];
      setApplications(apps);

      // Load group chat if there are selected helpers
      const selectedHelpers = apps.filter(a => a.status === 'selected');
      if (selectedHelpers.length > 0) {
        loadGroupChat();
        checkPrivateUnread(selectedHelpers);
      }
    } catch (error) {
      console.error('Failed to load request:', error);
      showToast('Failed to load request details', 'error');
    } finally {
      setLoading(false);
    }
  };


  const loadGroupChat = async () => {
    setGroupChatLoading(true);
    try {
      const response = await chatAPI.getGroupConversation(id);
      if (response.data) {
        setGroupChatData(response.data);
        setGroupMessages(response.data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load group chat:', error);
    } finally {
      setGroupChatLoading(false);
    }
  };

  const checkPrivateUnread = async (helpers) => {
    const unreadMap = {};
    for (const helper of helpers) {
      try {
        const response = await chatAPI.getRequestConversation(id, helper.user_id);
        if (response.data?.messages) {
          const hasUnread = response.data.messages.some(
            m => m.sender_id !== user?.id && !(m.read_by || []).includes(user?.id)
          );
          if (hasUnread) {
            unreadMap[helper.user_id] = true;
          }
        }
      } catch (e) {
        // Ignore errors for individual helpers
      }
    }
    setPrivateUnreadByHelper(unreadMap);
  };

  const handleSendGroupMessage = async () => {
    if (!groupNewMessage.trim() || groupSendingMessage) return;

    setGroupSendingMessage(true);
    const messageContent = groupNewMessage.trim();
    setGroupNewMessage('');

    try {
      let chatId = groupChatData?.id;
      if (!chatId) {
        const convResponse = await chatAPI.getGroupConversation(id);
        if (convResponse.data) {
          setGroupChatData(convResponse.data);
          chatId = convResponse.data.id;
        }
      }

      if (!chatId) {
        throw new Error('Could not create conversation');
      }

      const response = await chatAPI.sendMessage(chatId, messageContent);
      setGroupMessages(prev => [...prev, response.data]);
      setTimeout(() => groupMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      setGroupNewMessage(messageContent);
      showToast('Failed to send message', 'error');
    } finally {
      setGroupSendingMessage(false);
    }
  };

  // Private chat functions
  const openPrivateChat = async (helper) => {
    setPrivateModalHelper(helper);
    setPrivateChatLoading(true);
    setPrivateMessages([]);
    // Clear unread indicator for this helper
    setPrivateUnreadByHelper(prev => ({ ...prev, [helper.user_id]: false }));

    try {
      const response = await chatAPI.getRequestConversation(id, helper.user_id);
      if (response.data) {
        setPrivateChatData(response.data);
        setPrivateMessages(response.data.messages || []);
        if (response.data.id) {
          chatAPI.markAsRead(response.data.id);
        }
        // Scroll to bottom after messages load
        setTimeout(() => privateMessagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
      }
    } catch (error) {
      console.error('Failed to load private chat:', error);
    } finally {
      setPrivateChatLoading(false);
    }
  };

  const closePrivateChat = () => {
    setPrivateModalHelper(null);
    setPrivateChatData(null);
    setPrivateMessages([]);
    setPrivateNewMessage('');
  };

  const handleSendPrivateMessage = async () => {
    if (!privateNewMessage.trim() || privateSendingMessage || !privateModalHelper) return;

    setPrivateSendingMessage(true);
    const messageContent = privateNewMessage.trim();
    setPrivateNewMessage('');

    try {
      let chatId = privateChatData?.id;
      if (!chatId) {
        const convResponse = await chatAPI.getRequestConversation(id, privateModalHelper.user_id);
        if (convResponse.data) {
          setPrivateChatData(convResponse.data);
          chatId = convResponse.data.id;
        }
      }

      if (!chatId) throw new Error('Could not create conversation');

      const response = await chatAPI.sendMessage(chatId, messageContent);
      setPrivateMessages(prev => [...prev, response.data]);
      setTimeout(() => privateMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      setPrivateNewMessage(messageContent);
      showToast('Failed to send message', 'error');
    } finally {
      setPrivateSendingMessage(false);
    }
  };

  const handleSelectHelper = async (helperId) => {
    setActionLoading(true);
    try {
      await requestsAPI.selectHelper(id, helperId);
      showToast('Helper selected successfully!', 'success');
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to select helper', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;

    setActionLoading(true);
    try {
      await requestsAPI.cancel(id);
      showToast('Request cancelled', 'success');
      navigate('/owner/requests');
    } catch (error) {
      showToast(error.message || 'Failed to cancel request', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFulfillRequest = async () => {
    if (!window.confirm('Mark this request as fulfilled? This will close the request and reject any remaining pending applications.')) return;

    setActionLoading(true);
    try {
      await requestsAPI.fulfillRequest(id);
      showToast('Request fulfilled - no more helpers needed', 'success');
      loadRequestDetails();
    } catch (error) {
      showToast(error.message || 'Failed to fulfill request', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    // Show rating modal first - combine mark complete with rating
    setShowRatingModal(true);
  };

  const handleCompleteAndRate = async () => {
    const selectedHelpers = applications.filter(a => a.status === 'selected');

    if (ratingMode === 'team' || selectedHelpers.length === 1) {
      // Team rating mode
      if (rating === 0) {
        showToast('Please select a rating', 'error');
        return;
      }
      setSubmittingRating(true);
      try {
        // Mark as complete first
        await requestsAPI.markCompleted(id);
        // Submit same rating for all helpers
        for (const helper of selectedHelpers) {
          await ratingsAPI.submitForHelper(id, helper.user_id, rating, ratingComment);
        }
        showToast('Job completed! Thank you for your rating.', 'success');
        setShowRatingModal(false);
        setRating(0);
        setRatingComment('');
        loadRequestDetails();
      } catch (error) {
        showToast(error.message || 'Failed to complete', 'error');
      } finally {
        setSubmittingRating(false);
      }
    } else {
      // Individual rating mode
      const allRated = selectedHelpers.every(h => individualRatings[h.user_id]?.rating > 0);
      if (!allRated) {
        showToast('Please rate all helpers', 'error');
        return;
      }
      setSubmittingRating(true);
      try {
        // Mark as complete first
        await requestsAPI.markCompleted(id);
        // Submit individual ratings
        for (const helper of selectedHelpers) {
          const { rating: helperRating, comment: helperComment } = individualRatings[helper.user_id];
          await ratingsAPI.submitForHelper(id, helper.user_id, helperRating, helperComment || '');
        }
        showToast('Job completed! Thank you for your ratings.', 'success');
        setShowRatingModal(false);
        setIndividualRatings({});
        setRatingMode('team');
        loadRequestDetails();
      } catch (error) {
        showToast(error.message || 'Failed to complete', 'error');
      } finally {
        setSubmittingRating(false);
      }
    }
  };

  const handleSubmitRating = async () => {
    const selectedHelpers = applications.filter(a => a.status === 'selected');

    if (ratingMode === 'team') {
      if (rating === 0) {
        showToast('Please select a rating', 'error');
        return;
      }
      setSubmittingRating(true);
      try {
        // Submit same rating for all helpers
        for (const helper of selectedHelpers) {
          await ratingsAPI.submitForHelper(id, helper.user_id, rating, ratingComment);
        }
        showToast('Thank you for your rating!', 'success');
        setShowRatingModal(false);
        setRating(0);
        setRatingComment('');
        loadRequestDetails();
      } catch (error) {
        showToast(error.message || 'Failed to submit rating', 'error');
      } finally {
        setSubmittingRating(false);
      }
    } else {
      // Individual rating mode - submit all individual ratings
      const allRated = selectedHelpers.every(h => individualRatings[h.user_id]?.rating > 0);
      if (!allRated) {
        showToast('Please rate all helpers', 'error');
        return;
      }
      setSubmittingRating(true);
      try {
        for (const helper of selectedHelpers) {
          const { rating: helperRating, comment: helperComment } = individualRatings[helper.user_id];
          await ratingsAPI.submitForHelper(id, helper.user_id, helperRating, helperComment || '');
        }
        showToast('Thank you for your ratings!', 'success');
        setShowRatingModal(false);
        setIndividualRatings({});
        setCurrentIndividualIndex(0);
        loadRequestDetails();
      } catch (error) {
        showToast(error.message || 'Failed to submit ratings', 'error');
      } finally {
        setSubmittingRating(false);
      }
    }
  };

  const handleShowMoreInfo = async (userId) => {
    setShowMoreInfo(userId);
    setLoadingMoreInfo(true);
    try {
      const response = await usersAPI.getPublicProfile(userId);
      setMoreInfoData(response.data);
    } catch (error) {
      showToast('Failed to load profile', 'error');
      setShowMoreInfo(null);
    } finally {
      setLoadingMoreInfo(false);
    }
  };

  const handleToggleDocs = async (helperId) => {
    if (expandedDocs === helperId) {
      setExpandedDocs(null);
      return;
    }

    setExpandedDocs(helperId);

    if (!docsData[helperId]) {
      setLoadingDocs(helperId);
      try {
        const response = await helpersAPI.getHelperDocuments(helperId);
        setDocsData(prev => ({
          ...prev,
          [helperId]: response.data || []
        }));
      } catch (error) {
        console.error('Failed to load documents:', error);
        showToast('Failed to load documents', 'error');
        setDocsData(prev => ({
          ...prev,
          [helperId]: []
        }));
      } finally {
        setLoadingDocs(null);
      }
    }
  };

  const formatDocType = (type) => {
    const labels = {
      stcw95: 'STCW 95',
      certification: 'Certification',
      reference: 'Reference',
      recommendation: 'Recommendation',
      id_document: 'ID Document',
      cv: 'CV',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getStatusInfo = (status) => {
    const info = {
      pending: { color: 'yellow', label: 'Pending', icon: Clock },
      active: { color: 'yellow', label: 'Pending', icon: Clock },
      matched: { color: 'green', label: 'Matched', icon: CheckCircle },
      confirmed: { color: 'green', label: 'Confirmed', icon: CheckCircle },
      in_progress: { color: 'blue', label: 'In Progress', icon: Ship },
      completed: { color: 'gray', label: 'Completed', icon: CheckCircle },
      cancelled: { color: 'red', label: 'Cancelled', icon: XCircle },
    };
    return info[status] || info.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-medium text-navy-900">Request not found</h2>
        <Link to="/owner/requests" className="btn-primary mt-4 inline-block">
          Back to Requests
        </Link>
      </div>
    );
  }

  const statusInfo = getStatusInfo(request.status);
  const StatusIcon = statusInfo.icon;
  const selectedHelpers = applications.filter(a => a.status === 'selected');
  const chatEnabled = selectedHelpers.length > 0 && ['pending', 'active', 'matched', 'in_progress', 'completed'].includes(request.status);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/owner/requests')}
          className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Requests
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy-900">
              {request.port_name}
            </h1>
            <div className="flex items-center mt-2 space-x-4 text-navy-600">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {format(new Date(request.scheduled_date), 'EEEE, MMM d, yyyy')}
              </div>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {request.scheduled_time}
              </div>
            </div>
          </div>
          <div className={`flex items-center px-4 py-2 rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
            <StatusIcon className="h-4 w-4 mr-2" />
            <span className="font-medium">{statusInfo.label}</span>
          </div>
        </div>
      </div>

      <div className={chatEnabled ? "grid md:grid-cols-2 gap-6" : ""}>
      {/* Request Details Card */}
      <div className="card">
        <h2 className="text-lg font-semibold tracking-tight text-navy-900 mb-4">
          Request Details
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex items-start">
            <Ship className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-navy-500">Yacht</p>
              <p className="font-medium text-navy-900">{request.yacht_name || 'Not specified'}</p>
              {request.yacht_length && (
                <p className="text-sm text-navy-600">{request.yacht_length}m</p>
              )}
            </div>
          </div>
          <div className="flex items-start">
            <Users className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-navy-500">Helpers Needed</p>
              <p className="font-medium text-navy-900">
                {request.helpers_confirmed > 0 ? (
                  <span>
                    <span className="text-green-600">{request.helpers_confirmed}</span>
                    {' / '}{request.helpers_needed} confirmed
                  </span>
                ) : (
                  request.helpers_needed
                )}
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <MapPin className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-navy-500">Mooring Type</p>
              <p className="font-medium text-navy-900 capitalize">
                {request.mooring_type?.replace('_', ' ') || 'Not specified'}
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <Clock className="h-5 w-5 text-navy-400 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-navy-500">Request Type</p>
              <p className="font-medium text-navy-900 capitalize">
                {request.request_type?.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>

        {request.notes && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-navy-500 mb-1">Notes</p>
            <p className="text-navy-700">{request.notes}</p>
          </div>
        )}
      </div>

      {/* Group Chat Section - chat with all selected helpers */}
      {chatEnabled && (
      <div className="card flex flex-col" style={{ minHeight: '400px' }}>
        <h2 className="text-lg font-semibold tracking-tight text-navy-900 flex items-center mb-4">
          <MessageCircle className="h-5 w-5 mr-2" />
          Group Chat
          <span className="text-sm font-normal text-navy-500 ml-2">
            (You + {selectedHelpers.length} helper{selectedHelpers.length !== 1 ? 's' : ''})
          </span>
        </h2>

        {groupChatLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="spinner w-6 h-6" />
          </div>
        ) : (
          <>
            <div className="chat-messages flex-1 space-y-3 mb-4 pr-1" style={{ maxHeight: '280px' }}>
              {groupMessages.length === 0 ? (
                <div className="text-center text-navy-400 py-8">
                  <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm mt-1">Start the group conversation</p>
                </div>
              ) : (
                groupMessages.map((message) => {
                  if (message.message_type === 'system') {
                    return (
                      <div key={message.id} className="flex justify-center">
                        <div className="text-xs text-navy-400 bg-gray-100 rounded-full px-3 py-1">
                          {message.content}
                        </div>
                      </div>
                    );
                  }

                  const isOwn = message.sender_id === user?.id;
                  const senderName = isOwn ? 'You' : (message.first_name || 'Helper');
                  return (
                    <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className="max-w-[75%]">
                        <p className={`text-xs font-medium text-navy-600 mb-1 ${isOwn ? 'text-right' : ''}`}>
                          {senderName}
                        </p>
                        <div className={`chat-bubble ${isOwn ? 'sent' : 'received'}`}>
                          {message.content}
                        </div>
                        <p className={`text-xs text-navy-400 mt-1 ${isOwn ? 'text-right' : ''}`}>
                          {message.sent_at ? format(new Date(message.sent_at), 'MMM d, h:mm a') : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={groupMessagesEndRef} />
            </div>

            <div className="pt-3 border-t border-gray-100">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={groupNewMessage}
                  onChange={(e) => setGroupNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendGroupMessage()}
                  placeholder="Message all helpers..."
                  className="input-field flex-1 py-2"
                  disabled={groupSendingMessage}
                />
                <button
                  onClick={handleSendGroupMessage}
                  disabled={!groupNewMessage.trim() || groupSendingMessage}
                  className="btn-primary py-2 px-4"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      )}
      </div>

      {/* Helper Selection Progress - show when some helpers are selected but quota not reached */}
      {(request.status === 'pending' || request.status === 'active') &&
       request.helpers_confirmed > 0 &&
       request.helpers_confirmed < request.helpers_needed && (
        <div className="card bg-amber-50 border border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-amber-900 flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Helpers Selected: {request.helpers_confirmed} of {request.helpers_needed}
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Your request is still open for more helpers. If a selected helper can bring extra hands, you can fulfill the request now.
              </p>
            </div>
            <button
              onClick={handleFulfillRequest}
              disabled={actionLoading}
              className="btn-secondary bg-white hover:bg-amber-100 border-amber-300 text-amber-800"
            >
              <UserCheck className="h-4 w-4 mr-2" />
              Fulfill Request
            </button>
          </div>
        </div>
      )}

      {/* Helper Applications (show for all active statuses including completed for private chat) */}
      {(request.status === 'pending' || request.status === 'active' || request.status === 'matched' || request.status === 'in_progress' || request.status === 'completed') && (
        <div>
          <h2 className="text-lg font-semibold text-navy-900 mb-4 tracking-tight">
            Applicants ({applications.length})
          </h2>

          {applications.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-apple-lg" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
              <Users className="h-10 w-10 text-navy-300 mx-auto mb-3" />
              <p className="text-navy-500">No applications yet</p>
              <p className="text-sm text-navy-400 mt-1 font-light">
                Helpers in this area will see your request and apply
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => {
                return (
                  <div key={app.id} className={`rounded-apple-lg overflow-hidden bg-white ${app.status === 'selected' ? 'ring-2 ring-green-400' : ''}`}
                    style={{ border: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    {/* Header: Photo + Name/Rating + Buttons */}
                    <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="h-14 w-14 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {app.profile_photo_url ? (
                            <img
                              src={app.profile_photo_url.startsWith('http') ? app.profile_photo_url : `${UPLOADS_BASE_URL}${app.profile_photo_url}`}
                              alt=""
                              className="h-14 w-14 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-navy-600 font-medium text-lg">
                              {app.first_name?.[0]}{app.last_name?.[0]}
                            </span>
                          )}
                        </div>
                        <div className="ml-4 min-w-0">
                          <p className="font-medium text-navy-900 text-base">
                            {app.first_name} {app.last_name}
                          </p>
                          <div className="flex items-center text-sm text-navy-500 mt-0.5">
                            <Star className="h-3.5 w-3.5 text-gold-500 fill-current mr-1" />
                            <span className="font-medium text-navy-700">{app.average_rating ? app.average_rating.toFixed(1) : 'New'}</span>
                            {app.rating_count > 0 && <span className="ml-1 text-navy-400">({app.rating_count} review{app.rating_count !== 1 ? 's' : ''})</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 items-center">
                        <button
                          onClick={() => handleShowMoreInfo(app.user_id)}
                          className="text-sm text-primary-600 hover:text-primary-700 font-normal py-2 px-3.5 rounded-full border border-primary-600 transition-all duration-300 inline-flex items-center"
                          title="View full profile"
                        >
                          <Info className="h-3.5 w-3.5 mr-1.5" />
                          More Info
                        </button>
                        <button
                          onClick={() => handleToggleDocs(app.helper_id)}
                          className={`text-sm font-normal py-2 px-3.5 rounded-full border transition-all duration-300 inline-flex items-center ${expandedDocs === app.helper_id ? 'bg-primary-50 border-primary-600 text-primary-700' : 'border-navy-300 text-navy-600 hover:border-navy-400'}`}
                          title="View documents"
                        >
                          <FileText className="h-3.5 w-3.5 mr-1.5" />
                          Docs
                          {expandedDocs === app.helper_id ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
                        </button>
                        {app.status === 'selected' && (
                          <button
                            onClick={() => openPrivateChat(app)}
                            className="text-sm font-normal py-2 px-3.5 rounded-full border border-primary-600 text-primary-600 hover:bg-primary-50 transition-all duration-300 inline-flex items-center relative"
                          >
                            <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                            Private Chat
                            {privateUnreadByHelper[app.user_id] && (
                              <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full border-2 border-white"></span>
                            )}
                          </button>
                        )}
                        {app.status === 'selected' ? (
                          <span className="inline-flex items-center text-green-700 font-medium bg-green-50 px-4 py-2 rounded-full text-sm">
                            <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                            Selected
                          </span>
                        ) : app.status === 'pending' ? (
                          <button
                            onClick={() => handleSelectHelper(app.helper_id)}
                            disabled={actionLoading || (request.helpers_confirmed >= request.helpers_needed)}
                            className="btn-primary py-2 px-5 text-sm disabled:opacity-50"
                          >
                            Select
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="px-5 pb-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                      <div className="grid grid-cols-3 gap-3 pt-4">
                        <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                          <p className="text-lg font-semibold text-navy-900">{app.total_jobs || 0}</p>
                          <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Jobs</p>
                        </div>
                        <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                          <p className="text-lg font-semibold text-navy-900">{app.experience_years || 0}</p>
                          <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Yrs Exp</p>
                        </div>
                        <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                          <p className="text-lg font-semibold text-navy-900">{app.rating_count || 0}</p>
                          <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Reviews</p>
                        </div>
                      </div>
                    </div>

                    {/* Languages */}
                    {app.languages && app.languages.length > 0 && (
                      <div className="px-5 pb-4 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Languages</p>
                        <p className="text-sm text-navy-700">{app.languages.join(' \u2022 ')}</p>
                      </div>
                    )}

                    {/* Reviews */}
                    {app.reviews && app.reviews.length > 0 && (
                      <div className="px-5 pb-4 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Recent Reviews</p>
                        <div className="space-y-2">
                          {app.reviews.map((review, idx) => (
                            <div key={idx} className="bg-navy-50 rounded-apple p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-navy-700">{review.from_name}</span>
                                <div className="flex items-center">
                                  <Star className="h-3 w-3 text-gold-500 fill-current" />
                                  <span className="ml-1 text-sm font-medium text-navy-700">{review.rating}</span>
                                </div>
                              </div>
                              <p className="text-sm text-navy-600 italic">"{review.comment}"</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Certifications */}
                    {app.certifications && (
                      <div className="px-5 pb-4 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Certifications</p>
                        <div className="flex items-center text-sm text-navy-700">
                          <Award className="h-4 w-4 mr-1.5 text-primary-600 flex-shrink-0" />
                          {app.certifications}
                        </div>
                      </div>
                    )}

                    {/* Skills */}
                    {app.skills && app.skills.length > 0 && (
                      <div className="px-5 pb-4 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Skills</p>
                        <div className="flex flex-wrap gap-1.5">
                          {app.skills.slice(0, 5).map((skill, idx) => (
                            <span key={idx} className="px-3 py-1 bg-navy-100 text-navy-700 text-xs rounded-full">
                              {skill}
                            </span>
                          ))}
                          {app.skills.length > 5 && (
                            <span className="px-3 py-1 text-navy-400 text-xs">+{app.skills.length - 5} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Application Message */}
                    {app.cover_message && (
                      <div className="px-5 pb-5 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Application Message</p>
                        <div className="bg-navy-50 rounded-apple p-3.5">
                          <p className="text-sm text-navy-700 italic leading-relaxed">"{app.cover_message}"</p>
                        </div>
                      </div>
                    )}

                    {/* Documents Panel */}
                    {expandedDocs === app.helper_id && (
                      <div className="bg-navy-50 p-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                        <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-3">Documents</p>
                        {loadingDocs === app.helper_id ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="spinner w-6 h-6" />
                          </div>
                        ) : (docsData[app.helper_id] || []).length === 0 ? (
                          <div className="text-center text-navy-400 py-6">
                            <FileText className="h-7 w-7 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No documents uploaded</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {docsData[app.helper_id].map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-apple">
                                <div className="flex items-center min-w-0">
                                  <div className="h-9 w-9 bg-primary-50 rounded-apple flex items-center justify-center flex-shrink-0">
                                    <FileText className="h-4 w-4 text-primary-600" />
                                  </div>
                                  <div className="ml-3 min-w-0">
                                    <p className="text-sm font-medium text-navy-900 truncate">{doc.file_name}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs text-primary-600">{formatDocType(doc.document_type)}</span>
                                      <span className="text-xs text-navy-400">{format(new Date(doc.uploaded_at), 'MMM d, yyyy')}</span>
                                    </div>
                                  </div>
                                </div>
                                <a
                                  href={`${UPLOADS_BASE_URL}${doc.file_path}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-full transition-colors duration-300 ml-2 flex-shrink-0"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {(request.status === 'pending' || request.status === 'active') && (
        <div className="flex justify-end space-x-4">
          <Link
            to={`/owner/requests/${id}/edit`}
            className="btn-secondary inline-flex items-center"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Modify Request
          </Link>
          <button
            onClick={handleCancel}
            disabled={actionLoading}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Cancel Request
          </button>
        </div>
      )}

      {/* Actions for matched requests */}
      {request.status === 'matched' && (
        <div className="flex justify-end space-x-4">
          <Link
            to={`/owner/requests/${id}/edit`}
            className="btn-secondary inline-flex items-center"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Modify Request
          </Link>
          <button
            onClick={handleCancel}
            disabled={actionLoading}
            className="text-red-600 hover:text-red-700 font-medium"
          >
            Cancel Request
          </button>
        </div>
      )}

      {/* Mark Complete Section for matched requests */}
      {request.status === 'matched' && !request.owner_rated && (
        <div className="card bg-blue-50 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Job Ready to Complete?</h3>
              <p className="text-sm text-blue-700 mt-1">
                {request.owner_completed
                  ? 'You marked this complete. Waiting for helper to confirm.'
                  : 'Once the mooring assistance is done, mark this job as complete.'}
              </p>
            </div>
            {!request.owner_completed ? (
              <button
                onClick={handleMarkComplete}
                disabled={actionLoading}
                className="btn-primary"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Complete
              </button>
            ) : (
              <span className="inline-flex items-center text-blue-600 font-medium">
                <CheckCircle className="h-5 w-5 mr-1" />
                Waiting for helper
              </span>
            )}
          </div>
        </div>
      )}

      {/* Completed Status */}
      {request.status === 'completed' && (
        <div className="card bg-green-50 border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <h3 className="font-semibold text-green-900">Job Completed!</h3>
                <p className="text-sm text-green-700">This mooring assistance has been completed.</p>
              </div>
            </div>
            {request.owner_rated ? (
              <span className="inline-flex items-center text-green-600 font-medium">
                <Star className="h-5 w-5 mr-1 text-gold-500" />
                Rated
              </span>
            ) : (
              <button
                onClick={() => setShowRatingModal(true)}
                className="btn-primary"
              >
                <Star className="h-4 w-4 mr-2" />
                Rate Helper
              </button>
            )}
          </div>
          {request.received_rating && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`h-4 w-4 ${s <= request.received_rating.rating ? 'text-gold-500 fill-current' : 'text-gray-300'}`} />
                ))}
                <span className="text-sm text-green-800 ml-1">({request.received_rating.rating}.0)</span>
              </div>
              {request.received_rating.comment && (
                <p className="text-sm text-green-800 italic">"{request.received_rating.comment}"</p>
              )}
              <p className="text-xs text-green-600 mt-1">
                — {request.received_rating.first_name} {request.received_rating.last_name}, {format(new Date(request.received_rating.created_at), 'MMM d, yyyy')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold tracking-tight text-navy-900 mb-4">
              {request.status === 'matched' ? 'Complete Job & Rate Helpers' : 'Rate the Helpers'}
            </h2>

            {/* Rating Mode Choice - only show if multiple helpers */}
            {selectedHelpers.length > 1 && (
              <div className="mb-6">
                <p className="text-sm text-navy-600 mb-3">How would you like to rate?</p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setRatingMode('team')}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                      ratingMode === 'team'
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-navy-600 hover:border-gray-300'
                    }`}
                  >
                    Same rating for all
                  </button>
                  <button
                    onClick={() => setRatingMode('individual')}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                      ratingMode === 'individual'
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-navy-600 hover:border-gray-300'
                    }`}
                  >
                    Rate individually
                  </button>
                </div>
              </div>
            )}

            {/* Team Rating Mode */}
            {(ratingMode === 'team' || selectedHelpers.length === 1) && (
              <>
                <p className="text-navy-600 mb-4">
                  {selectedHelpers.length > 1
                    ? `Rate all ${selectedHelpers.length} helpers together:`
                    : 'How was your experience with the mooring assistance?'}
                </p>

                {/* Star Rating */}
                <div className="flex justify-center space-x-2 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`h-10 w-10 transition-colors ${
                          star <= rating
                            ? 'text-gold-500 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>

                {/* Comment */}
                <textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder="Add a comment (optional)"
                  className="input-field w-full mb-4"
                  rows={3}
                />
              </>
            )}

            {/* Individual Rating Mode */}
            {ratingMode === 'individual' && selectedHelpers.length > 1 && (
              <div className="space-y-6">
                {selectedHelpers.map((helper, index) => (
                  <div key={helper.user_id} className="p-4 bg-navy-50 rounded-lg">
                    <p className="font-medium text-navy-900 mb-3">
                      {helper.first_name} {helper.last_name}
                    </p>

                    {/* Star Rating for this helper */}
                    <div className="flex justify-center space-x-2 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setIndividualRatings(prev => ({
                            ...prev,
                            [helper.user_id]: { ...prev[helper.user_id], rating: star }
                          }))}
                          className="focus:outline-none"
                        >
                          <Star
                            className={`h-8 w-8 transition-colors ${
                              star <= (individualRatings[helper.user_id]?.rating || 0)
                                ? 'text-gold-500 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>

                    {/* Comment for this helper */}
                    <textarea
                      value={individualRatings[helper.user_id]?.comment || ''}
                      onChange={(e) => setIndividualRatings(prev => ({
                        ...prev,
                        [helper.user_id]: { ...prev[helper.user_id], comment: e.target.value }
                      }))}
                      placeholder="Add a comment (optional)"
                      className="input-field w-full text-sm"
                      rows={2}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowRatingModal(false);
                  setRating(0);
                  setRatingComment('');
                  setIndividualRatings({});
                  setRatingMode('team');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={request.status === 'matched' ? handleCompleteAndRate : handleSubmitRating}
                disabled={
                  (ratingMode === 'team' && rating === 0) ||
                  (ratingMode === 'individual' && !selectedHelpers.every(h => individualRatings[h.user_id]?.rating > 0)) ||
                  submittingRating
                }
                className="btn-primary flex-1"
              >
                {submittingRating ? 'Submitting...' : (request.status === 'matched' ? 'Complete & Rate' : 'Submit Rating')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* More Info Modal for Helper Profile */}
      {showMoreInfo && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-apple-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.16)' }}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white rounded-t-apple-xl px-6 py-4 flex items-center justify-between"
              style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
            >
              <h2 className="text-lg font-semibold text-navy-900">Helper Profile</h2>
              <button
                onClick={() => { setShowMoreInfo(null); setMoreInfoData(null); }}
                className="p-1.5 text-navy-400 hover:text-navy-700 hover:bg-navy-100 rounded-full transition-colors duration-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingMoreInfo ? (
              <div className="flex items-center justify-center py-16">
                <div className="spinner w-8 h-8" />
              </div>
            ) : moreInfoData ? (
              <div className="px-6 py-5">
                {/* Header: Photo + Name + Rating */}
                <div className="flex items-center mb-5">
                  <div className="h-16 w-16 bg-navy-100 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {moreInfoData.profile_photo_url ? (
                      <img src={moreInfoData.profile_photo_url} alt="" className="h-16 w-16 rounded-full object-cover" />
                    ) : (
                      <span className="text-navy-600 font-medium text-xl">
                        {moreInfoData.first_name?.[0]}{moreInfoData.last_name?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-lg font-medium text-navy-900">
                      {moreInfoData.first_name} {moreInfoData.last_name}
                    </p>
                    <div className="flex items-center text-sm text-navy-500 mt-0.5">
                      {moreInfoData.helper_profile?.average_rating > 0 ? (
                        <>
                          <Star className="h-3.5 w-3.5 text-gold-500 fill-current mr-1" />
                          <span className="font-medium text-navy-700">{moreInfoData.helper_profile.average_rating.toFixed(1)}</span>
                          <span className="text-navy-400 ml-1">({moreInfoData.helper_profile.rating_count} review{moreInfoData.helper_profile.rating_count !== 1 ? 's' : ''})</span>
                        </>
                      ) : (
                        <span className="text-navy-400">No ratings yet</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {moreInfoData.helper_profile?.bio && (
                  <div className="bg-navy-50 rounded-apple p-4 mb-5">
                    <p className="text-sm text-navy-700 leading-relaxed">{moreInfoData.helper_profile.bio}</p>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                    <p className="text-xl font-semibold text-navy-900">{moreInfoData.helper_profile?.total_jobs || 0}</p>
                    <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Jobs</p>
                  </div>
                  <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                    <p className="text-xl font-semibold text-navy-900">{moreInfoData.helper_profile?.experience_years || 0}</p>
                    <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Yrs Exp</p>
                  </div>
                  <div className="bg-navy-50 rounded-apple py-3 px-2 text-center">
                    <p className="text-xl font-semibold text-navy-900">{moreInfoData.months_active || 0}</p>
                    <p className="text-[11px] text-navy-500 uppercase tracking-wider mt-0.5">Months</p>
                  </div>
                </div>

                {/* Languages */}
                {moreInfoData.helper_profile?.languages && moreInfoData.helper_profile.languages.length > 0 && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Languages</p>
                    <p className="text-sm text-navy-700">{moreInfoData.helper_profile.languages.join(' \u2022 ')}</p>
                  </div>
                )}

                {/* Skills */}
                {moreInfoData.helper_profile?.skills && moreInfoData.helper_profile.skills.length > 0 && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Skills</p>
                    <div className="flex flex-wrap gap-1.5">
                      {moreInfoData.helper_profile.skills.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-navy-100 text-navy-700 text-xs rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certifications */}
                {moreInfoData.helper_profile?.certifications && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-2">Certifications</p>
                    <div className="flex items-center text-sm text-navy-700">
                      <Award className="h-4 w-4 mr-1.5 text-primary-600 flex-shrink-0" />
                      {moreInfoData.helper_profile.certifications}
                    </div>
                  </div>
                )}

                {/* Reviews */}
                {moreInfoData.references && moreInfoData.references.length > 0 && (
                  <div className="mb-5 pt-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                    <p className="text-[11px] font-medium text-navy-400 uppercase tracking-wider mb-3">Recent Reviews</p>
                    <div className="space-y-2">
                      {moreInfoData.references.map((ref, idx) => (
                        <div key={idx} className="bg-navy-50 rounded-apple p-3.5">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-medium text-navy-800">{ref.from_name}</span>
                            <div className="flex items-center">
                              <Star className="h-3 w-3 text-gold-500 fill-current" />
                              <span className="ml-1 text-sm font-medium text-navy-700">{ref.rating}</span>
                            </div>
                          </div>
                          <p className="text-sm text-navy-600 italic leading-relaxed">"{ref.comment}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Member Since */}
                <div className="text-center text-xs text-navy-400 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                  Member since {format(new Date(moreInfoData.member_since || moreInfoData.created_at), 'MMMM yyyy')}
                </div>
              </div>
            ) : (
              <p className="text-center text-navy-400 py-12">Failed to load profile</p>
            )}
          </div>
        </div>
      )}

      {/* Private Chat Modal */}
      {privateModalHelper && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-navy-900">Private Chat</h2>
                <p className="text-sm text-navy-500">with {privateModalHelper.first_name} {privateModalHelper.last_name}</p>
              </div>
              <button
                onClick={closePrivateChat}
                className="p-2 text-navy-400 hover:text-navy-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '400px' }}>
              {privateChatLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner w-6 h-6" />
                </div>
              ) : privateMessages.length === 0 ? (
                <div className="text-center text-navy-400 py-8">
                  <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No private messages yet</p>
                  <p className="text-sm mt-1">Start a private conversation</p>
                </div>
              ) : (
                privateMessages.map((message) => {
                  const isOwn = message.sender_id === user?.id;
                  return (
                    <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${isOwn ? 'bg-primary-600 text-white' : 'bg-gray-100 text-navy-900'} rounded-2xl px-4 py-2`}>
                        <p>{message.content}</p>
                        <p className={`text-xs mt-1 ${isOwn ? 'text-primary-200' : 'text-navy-400'}`}>
                          {message.sent_at ? format(new Date(message.sent_at), 'MMM d, h:mm a') : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={privateMessagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-100">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={privateNewMessage}
                  onChange={(e) => setPrivateNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendPrivateMessage()}
                  placeholder="Type a private message..."
                  className="input-field flex-1 py-2"
                  disabled={privateSendingMessage}
                />
                <button
                  onClick={handleSendPrivateMessage}
                  disabled={!privateNewMessage.trim() || privateSendingMessage}
                  className="btn-primary py-2 px-4"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default RequestDetails;
